import {
  ResetPasswordVariables,
  LoginVariables,
  User,
  VerifySMSCodeVariables,
  RegisterUserCredentials,
  RegisterResponse,
  VerificationCodeResponse,
  SmsAuthVariables,
  BaseAuthClient,
} from "@core/types";
import { Customer } from "./types";
import { supabaseClient } from "../supabase";
import { SupaCustomer } from "../types/supabaseFragments";
import { LocalStorageOperations } from "../../../../data/storage/LocalStorageOperations";


class AuthClient extends BaseAuthClient {
  async setToken(token: string, supabaseAuth?: { accessToken: string; refreshToken: string }) {
    this.instance.token = token;
    
    if (supabaseAuth) {
      await supabaseClient.auth.setSession({
        access_token: supabaseAuth.accessToken,
        refresh_token: supabaseAuth.refreshToken,
      });
    }
  }

  async sendVerificationCode(phone: string): Promise<VerificationCodeResponse> {
    const { data } = await this.instance.post<any>("/wp-json/parhato-api/v1/reset-password", { phone });

    return { verificationCodeId: data.code_id, phoneNumber: data.phone_number };
  }

  async resetPassword(params: ResetPasswordVariables) {
    const res = await this.instance.post<any>("/wp-json/parhato-api/v1/reset-password", {
      email: params.email,
      phone: params.phone,
      new_password: params.newPassword,
      verification_code: params.verificationCode,
      verification_code_id: params.verificationCodeId,
    });

    return res.status;
  }

  async verifyCode(params: VerifySMSCodeVariables) {
    const res = await this.instance.post<any>("/wp-json/parhato-api/v1/verify-code", params);

    return res.status;
  }

  async register(params: RegisterUserCredentials): Promise<RegisterResponse> {
    const credentials = {
      first_name: params.firstName,
      last_name: params.lastName,
      phone: params.phone,
      password: params.password,
      referral_link: params.referal?.referralLink ?? "",
      own_version_referral: params.referal?.ownVersionReferral ?? "",
      verification_code: params.verification?.verificationCode ?? "",
      verification_code_id: params.verification?.verificationCodeId ?? "",
    };

    const { data } = await this.instance.post<any>("/wp-json/parhato-api/v1/register", credentials);

    const user: RegisterResponse = {
      userName: data.user_login,
    };

    return user;
  }

  async getSmsCode(phone: string): Promise<VerificationCodeResponse> {
    const { data } = await this.instance.post<any>("/wp-json/parhato-api/v1/auth", { phone });

    return {
      verificationCodeId: data.verificationCodeId,
      phoneNumber: data.phone,
    };
  }

  async getUcallerCode(phone: string): Promise<VerificationCodeResponse> {
    const { data } = await this.instance.post<any>("/wp-json/parhato-api/v1/auth", { phone, method: "ucaller" });

    return {
      verificationCodeId: data.verificationCodeId,
      phoneNumber: data.phone,
    };
  }

  async authBySmsCode(params: SmsAuthVariables) {
    const { data: authInfo } = await this.instance.post<any>("/wp-json/parhato-api/v1/auth", {
      first_name: params.firstName,
      last_name: params.lastName,
      phone: params.phone,
      verification_code: params.verificationCode,
      verification_code_id: params.verificationCodeId,
    });

    const { data: profileData } = await this.instance.get<Customer>(`/wp-json/wc/v3/customers/${authInfo.data.id}`);

    const user: User = {
      id: profileData.id.toString(),
      userName: profileData.username,
      firstName: profileData.first_name,
      lastName: profileData.last_name,
      email: profileData.email,
      phone: profileData.billing.phone,
      token: authInfo.data.supabaseSession?.accessToken ?? "",
      supabaseAuth: authInfo.data.supabaseSession,
    };

    return user;
  }

  async getRegisterVerificationCode(phone: string): Promise<VerificationCodeResponse> {
    const { data } = await this.instance.post<any>("/wp-json/parhato-api/v1/register", { phone });
    return { verificationCodeId: data.code_id, phoneNumber: data.phone_number };
  }

  async getSupabaseSessionWithJWT() {
    if (!this.instance.token) {
      throw new Error("No JWT token available");
    }

    const { data, error } = await supabaseClient.functions.invoke<{
      accessToken: string;
      refreshToken: string;
    }>("get-supabase-session", {
      method: "POST",
      headers: {
        "Wp-Authorization": `Bearer ${this.instance.token}`,
      },
    });

    if (error || !data) {
      throw new Error("Failed to get Supabase session");
    }

    this.setToken(data.accessToken, data);
    LocalStorageOperations.updateUserInfo({ token: data.accessToken, supabaseAuth: data });
  }

  async getTGVerificationToken(): Promise<{ token: string }> {
    const token = await supabaseClient.functions.invoke<{ token: string }>("telegram-auth", {
      method: "GET",
    });

    return token.data!;
  }

  async authByTGToken(token: string): Promise<User | { customer_confirmed: false }> {
    const { data: authData } = await supabaseClient.functions.invoke<
      | (SupaCustomer & { token: string; supabaseSession: { accessToken: string; refreshToken: string } })
      | { customer_confirmed: false }
    >("telegram-auth", {
      method: "POST",
      body: {
        verification_token: token,
      },
    });

    if (!authData) {
      throw new Error("Failed to authenticate with Telegram");
    }

    if ("customer_confirmed" in authData) {
      return { customer_confirmed: false };
    }

    const user: User = {
      id: authData.id.toString(),
      userName: authData.username ?? "",
      firstName: authData.first_name ?? "",
      lastName: authData.last_name ?? "",
      email: authData.email ?? "",
      phone: authData.phone ?? "",
      token: authData.supabaseSession.accessToken ?? "",
      supabaseAuth: authData.supabaseSession,
    };

    return user;
  }

  async initAuth() {
    if (!this.instance.token) {
      return;
    }

    const { data } = await supabaseClient.auth.getSession();

    if (!data.session) {
      await this.getSupabaseSessionWithJWT();
    }
  }

  async logout() {
    this.instance.token = undefined;
    await supabaseClient.auth.signOut();
  }
}

export default AuthClient;
